/**
 * Aiká — Ingredientes com scroll horizontal
 *
 * Desktop (>=750px, sticky ligado, sem prefers-reduced-motion):
 *   a section ganha altura extra = overflow horizontal da trilha.
 *   Enquanto essa altura extra "passa" pela viewport, o wrapper fica
 *   sticky e o scroll vertical é convertido em transform: translate3d()
 *   horizontal na trilha, via requestAnimationFrame.
 *
 * Mobile / sticky desligado / reduced-motion:
 *   nenhuma medição roda — a trilha vira scroll horizontal nativo comum
 *   (o CSS já cuida disso com overflow-x auto + scroll-snap).
 *
 * Suporta múltiplas instâncias na mesma página e reage aos eventos
 * do editor de tema do Shopify (load / unload / block:select).
 */
(function () {
  var instances = {};

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      var args = arguments;
      t = setTimeout(function () {
        fn.apply(null, args);
      }, wait);
    };
  }

  function createInstance(root) {
    if (!root || root.dataset.ingredientsInitialized === 'true') return null;

    var stickyWrap = root.querySelector('[data-ingredients-sticky-wrap]');
    var viewport = root.querySelector('[data-ingredients-track-viewport]');
    var track = root.querySelector('[data-ingredients-track]');
    if (!stickyWrap || !viewport || !track) return null;

    root.dataset.ingredientsInitialized = 'true';

    var stickyEnabled = root.dataset.stickyEnabled === 'true';
    var mediaQuery = window.matchMedia('(min-width: 750px)');
    var reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    var horizontalOverflow = 0;
    var active = false;
    var ticking = false;

    function isDesktop() {
      return mediaQuery.matches;
    }

    function prefersReducedMotion() {
      return reducedMotionQuery.matches;
    }

    function measure() {
      var trackWidth = track.scrollWidth;
      var viewportWidth = viewport.clientWidth;
      horizontalOverflow = Math.max(trackWidth - viewportWidth, 0);
      if (horizontalOverflow > 0) {
        // Margem de segurança: cobre a ilustração do último card, que
        // pode se estender além da borda do card, e qualquer imprecisão
        // de medição entre a primeira passada e o carregamento completo.
        horizontalOverflow += 80;
      }

      if (active && horizontalOverflow > 0) {
        root.style.height = 'calc(' + getComputedStyle(root).getPropertyValue('--aika-ing-sticky-height') + ' + ' + horizontalOverflow + 'px)';
      } else {
        root.style.height = '';
      }
    }

    function update() {
      ticking = false;
      if (!active || horizontalOverflow === 0) return;

      var rect = root.getBoundingClientRect();
      var raw = -rect.top;
      var progress = raw / horizontalOverflow;
      if (progress < 0) progress = 0;
      if (progress > 1) progress = 1;

      track.style.transform = 'translate3d(' + (-progress * horizontalOverflow) + 'px, 0, 0)';
    }

    function requestUpdate() {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(update);
      }
    }

    function evaluateActive() {
      var shouldBeActive = stickyEnabled && isDesktop() && !prefersReducedMotion();

      if (shouldBeActive !== active) {
        active = shouldBeActive;
        if (!active) {
          track.style.transform = '';
          root.style.height = '';
        }
      }
      measure();
      requestUpdate();
    }

    var onScroll = function () {
      requestUpdate();
    };
    var onResize = debounce(evaluateActive, 150);
    var onMediaChange = function () {
      evaluateActive();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', onMediaChange);
      reducedMotionQuery.addEventListener('change', onMediaChange);
    }

    // Foco de bloco no editor: garante que o card selecionado fique visível,
    // deslocando a trilha até ele (sem depender do scroll da página).
    function focusBlock(blockId) {
      var card = track.querySelector('[data-block-id="' + blockId + '"]');
      if (!card) return;

      if (active) {
        var cardCenter = card.offsetLeft + card.offsetWidth / 2;
        var viewportWidth = viewport.clientWidth;
        var target = Math.min(
          Math.max(cardCenter - viewportWidth / 2, 0),
          horizontalOverflow
        );
        track.style.transform = 'translate3d(' + -target + 'px, 0, 0)';
        card.scrollIntoView({ block: 'nearest' });
      } else {
        card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }

    evaluateActive();

    // Remede depois que cada imagem da trilha terminar de carregar —
    // a primeira medição (no DOMContentLoaded) pode rodar antes de o
    // navegador saber o tamanho final de tudo.
    var trackImages = track.querySelectorAll('img');
    trackImages.forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', evaluateActive, { once: true });
    });
    window.addEventListener('load', evaluateActive, { once: true });

    return {
      root: root,
      evaluateActive: evaluateActive,
      focusBlock: focusBlock,
      destroy: function () {
        window.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', onResize);
        if (mediaQuery.removeEventListener) {
          mediaQuery.removeEventListener('change', onMediaChange);
          reducedMotionQuery.removeEventListener('change', onMediaChange);
        }
        root.style.height = '';
        track.style.transform = '';
        delete root.dataset.ingredientsInitialized;
      },
    };
  }

  function initAll(context) {
    var scope = context || document;
    scope.querySelectorAll('[data-ingredients-section]').forEach(function (root) {
      var sectionId = root.id.replace('shopify-section-', '');
      if (instances[sectionId]) return;
      var instance = createInstance(root);
      if (instance) instances[sectionId] = instance;
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initAll(document);
  });

  // Editor do tema Shopify: recarrega/limpa instâncias quando a section
  // é adicionada, editada ou removida sem um reload de página completo.
  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.querySelector
      ? event.target.querySelector('[data-ingredients-section]')
      : null;
    if (!root) return;
    var sectionId = root.id.replace('shopify-section-', '');
    if (instances[sectionId]) {
      instances[sectionId].destroy();
      delete instances[sectionId];
    }
  });

  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest
      ? event.target.closest('[data-ingredients-section]')
      : null;
    if (!root) return;
    var sectionId = root.id.replace('shopify-section-', '');
    var instance = instances[sectionId];
    if (instance && event.detail && event.detail.blockId) {
      instance.focusBlock(event.detail.blockId);
    }
  });
})();
